import { useEffect, useMemo, useRef, useState } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';

export default function useEnsureTripLock({
  tripId,
  lockKey
}) {

  const socket = useSocketStore(state => state.socket);

  const [blocked, setBlocked] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const [lockedBy, setLockedBy] = useState('');
  const [hasLock, setHasLock] = useState(false);

  const hasLockRef = useRef(false);

  const blockedTxt = useMemo(() => {

    if (!blocked) {
      return '';
    }

    if (queuePosition) {
      return `🔒 ${lockedBy} está editando. Você está na fila (${queuePosition}).`;
    }

    return `🔒 ${lockedBy} está editando.`;

  }, [
    blocked,
    lockedBy,
    queuePosition
  ]);

  // LOCK INICIAL
  useEffect(() => {

    if (!lockKey || !socket) {
      return;
    }

    let isActive = true;

    const acquireLock = () => {

      if (!socket.connected || !isActive) {
        return;
      }

      hasLockRef.current = false;
      setHasLock(false);

      socket.emit(
        'join',
        `lock:${lockKey}`
      );

      socket.emit(
        'escala:lock',
        {
          lockKey,
          tripId
        },
        res => {

          if (!isActive || !socket.connected) {
            return;
          }

          if (res?.status === 'waiting') {

            hasLockRef.current = false;
            setHasLock(false);

            setLockedBy(res.user || '');
            setQueuePosition(res.position || null);
            setBlocked(true);

            return;
          }

          if (res?.status === 'ok') {

            hasLockRef.current = true;
            setHasLock(true);
            setLockedBy('');
            setQueuePosition(null);
            setBlocked(false);

            console.log('🟢 Lock adquirido');

          }

        }
      );

    };

    const handleDisconnect = () => {

      // O socket antigo não pode mais ser
      // considerado dono do lock.
      hasLockRef.current = false;
      setHasLock(false);

      setQueuePosition(null);

    };

    const handleConnect = () => {

      console.log('🔄 Reconectado, solicitando lock novamente');

      acquireLock();

    };

    socket.on('disconnect', handleDisconnect);
    socket.on('connect', handleConnect);

    // Montou e já está conectado
    if (socket.connected) {
      acquireLock();
    }

    return () => {

      isActive = false;

      socket.off('disconnect', handleDisconnect);
      socket.off('connect', handleConnect);

      if (socket.connected) {

        socket.emit(
          'escala:unlock',
          { lockKey }
        );

        socket.emit(
          'leave',
          `lock:${lockKey}`
        );

      }

      hasLockRef.current = false;

      console.log('🔓 Cleanup lock');

    };

  }, [socket, lockKey, tripId]);

  // LOCK TRANSFERIDO PARA MIM
  useEffect(() => {

    if (!socket || !lockKey) {
      return;
    }

    const handleLockGranted = ({ lockKey: grantedKey }) => {

      if (grantedKey !== lockKey) {
        return;
      }

      console.log('🟢 Lock transferido para mim');

      hasLockRef.current = true;
      setHasLock(true);

      setLockedBy('');
      setQueuePosition(null);

      setBlocked(false);
    };

    socket.on('trip:lock-granted', handleLockGranted);

    return () => {
      socket.off('trip:lock-granted', handleLockGranted);
    };

  }, [socket, lockKey]);

  // POSIÇÃO NA FILA
  useEffect(() => {

    if (!socket || !lockKey) {
      return;
    }

    const handleQueuePosition = ({
      lockKey: eventLockKey,
      position
    }) => {

      if (eventLockKey !== lockKey) {
        return;
      }

      if (hasLockRef.current) {
        return;
      }

      setQueuePosition(position);
    };

    socket.on('trip:queue-position', handleQueuePosition);

    return () => {
      socket.off('trip:queue-position', handleQueuePosition);
    };

  }, [socket, lockKey]);

  // OUTRO USUÁRIO ASSUMIU O LOCK
  useEffect(() => {

    if (!socket || !lockKey) {
      return;
    }

    const handleLocked = ({ lockKey: lockedKey, userName }) => {

      if (lockedKey !== lockKey) {
        return;
      }

      // eu sou o dono atual
      if (hasLockRef.current) {
        return;
      }

      console.log(`🔒 ${userName} assumiu o lock`);

      hasLockRef.current = false;
      setHasLock(false);

      setLockedBy(userName || '');

      setBlocked(true);
    };

    const handleUnlocked = ({ lockKey: unlockedKey }) => {

      if (unlockedKey !== lockKey) {
        return;
      }

      hasLockRef.current = false;
      setHasLock(false);

      setLockedBy('');
      setQueuePosition(null);

      setBlocked(false);
    };

    socket.on('trip:locked', handleLocked);
    socket.on('trip:unlocked', handleUnlocked);

    return () => {
      socket.off('trip:locked', handleLocked);
      socket.off('trip:unlocked', handleUnlocked);
    };

  }, [socket, lockKey]);

  return {
    blocked,
    blockedTxt,
    queuePosition,
    lockKey,
    hasLock
  };
}